/**
 * Tela de carregamento da entrada.
 *
 * ── Por que é real ──────────────────────────────────────────────────────
 * A barra não é um temporizador disfarçado. Cada arquivo é baixado com
 * `fetch` e lido em pedaços, e o progresso é a soma de bytes recebidos sobre
 * o total anunciado nos cabeçalhos. Quando marca 60%, 60% dos bytes chegaram
 * de verdade — numa conexão lenta ela demora, numa rápida ela voa, e é assim
 * que tem de ser.
 *
 * Barra figurativa é pior que nenhuma: ela mente sobre quanto falta, e o
 * visitante que confia nela desiste achando que travou.
 *
 * ── O que ela carrega ───────────────────────────────────────────────────
 * As fontes e o começo da sequência do hero. A sequência é o único peso real
 * da home (1,7 MB em 100 quadros), e sem ela a primeira rolagem mostra quadro
 * repetido enquanto os arquivos chegam. Carregamos os primeiros 30 — o
 * suficiente para a jogada começar fluida; o resto entra sozinho depois.
 *
 * ── Quando ela NÃO aparece ──────────────────────────────────────────────
 * Na segunda visita da sessão, e em tela pequena. No celular a sequência do
 * hero nem é usada, então segurar a entrada para baixar 600 KB seria cobrar
 * do visitante por algo que ele não vai ver.
 *
 * ── A trava de segurança ────────────────────────────────────────────────
 * Um teto de 8 segundos derruba a tela aconteça o que acontecer. Preloader
 * que depende de rede é candidato a prender o site inteiro num arquivo que
 * não responde — e site que não abre é pior que site sem vídeo.
 */

import { useEffect, useRef, useState } from 'react';
import { gsap, useGSAP, EASE } from '@/lib/motion';
import { LogoUCC } from './LogoUCC';

/** Quantos quadros da sequência entram na conta. */
/** Teto absoluto. Passou disso, a tela sai mesmo sem ter terminado. */
const LIMITE_MS = 8000;
const CHAVE_SESSAO = 'ucc:preload';

/*
 * A hero não usa mais a sequência de quadros, então eles saíram daqui.
 *
 * Eram trinta arquivos baixados antes de a página aparecer, para alimentar um
 * vídeo de fundo que não existe mais. Continuar buscando seria gastar a
 * primeira visita de cada sessão com meio megabyte que ninguém veria.
 */
const alvos = () => ['/logo.webp'];

/** Deve aparecer? Segunda visita da sessão e telas pequenas ficam de fora. */
export function devePreCarregar(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    if (sessionStorage.getItem(CHAVE_SESSAO) === '1') return false;
  } catch {
    // Sem sessionStorage (janela anônima restrita) seguimos em frente: mostrar
    // a tela de novo é melhor que quebrar a entrada.
  }
  return window.matchMedia('(min-width: 1024px)').matches;
}

interface Props {
  onPronto: () => void;
}

export function Preloader({ onPronto }: Props) {
  const raiz = useRef<HTMLDivElement>(null);
  const [pct, setPct] = useState(0);
  const [saindo, setSaindo] = useState(false);

  useEffect(() => {
    let vivo = true;
    const controlador = new AbortController();

    const encerrar = () => {
      if (!vivo) return;
      vivo = false;
      try {
        sessionStorage.setItem(CHAVE_SESSAO, '1');
      } catch {
        /* sem sessionStorage, só não lembramos */
      }
      setPct(100);
      setSaindo(true);
    };

    const teto = setTimeout(() => {
      controlador.abort();
      encerrar();
    }, LIMITE_MS);

    (async () => {
      const lista = alvos();
      const recebidos = new Array<number>(lista.length).fill(0);
      const totais = new Array<number>(lista.length).fill(0);

      /**
       * Enquanto o `content-length` de um arquivo não chega, ele conta como o
       * tamanho médio dos que já vieram. Sem isso a barra daria saltos —
       * cairia a cada arquivo novo entrando na conta com total zero.
       */
      const atualizar = () => {
        const conhecidos = totais.filter((t) => t > 0);
        const medio = conhecidos.length
          ? conhecidos.reduce((s, v) => s + v, 0) / conhecidos.length
          : 20_000;
        const total = totais.reduce((s, t) => s + (t > 0 ? t : medio), 0);
        const feito = recebidos.reduce((s, v) => s + v, 0);
        if (vivo) setPct(Math.min(99, Math.round((feito / Math.max(total, 1)) * 100)));
      };

      const baixar = async (url: string, i: number) => {
        try {
          const res = await fetch(url, { signal: controlador.signal });
          if (!res.ok || !res.body) return;
          const tam = Number(res.headers.get('content-length') ?? 0);
          if (tam > 0) totais[i] = tam;
          const leitor = res.body.getReader();
          let lidos = 0;
          for (;;) {
            const { done, value } = await leitor.read();
            if (done) break;
            lidos += value?.byteLength ?? 0;
            recebidos[i] = lidos;
            atualizar();
          }
          // Sem `content-length` (resposta comprimida em stream), o que foi
          // lido vira o total daquele arquivo.
          if (tam <= 0) totais[i] = lidos;
          atualizar();
        } catch {
          // Um arquivo que falha não pode segurar a entrada. Ele conta como
          // concluído para a barra não ficar parada em 97%.
          const jaLido = recebidos[i] ?? 0;
          const fechado = jaLido > 0 ? jaLido : 1;
          totais[i] = fechado;
          recebidos[i] = fechado;
          atualizar();
        }
      };

      // Em blocos de 6: mais que isso e o navegador enfileira sozinho, sem
      // ganho, e ainda atrasa os primeiros quadros — que são os que importam.
      for (let i = 0; i < lista.length; i += 6) {
        if (!vivo) return;
        await Promise.all(lista.slice(i, i + 6).map((u, k) => baixar(u, i + k)));
      }

      await document.fonts?.ready?.catch?.(() => undefined);
      encerrar();
    })();

    return () => {
      vivo = false;
      clearTimeout(teto);
      controlador.abort();
    };
  }, []);

  // Saída: a cortina sobe e revela a página. Só depois o React desmonta.
  useGSAP(
    () => {
      if (!saindo) return;
      const reduzido = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduzido) {
        onPronto();
        return;
      }
      gsap
        .timeline({ onComplete: onPronto })
        .to('[data-pre="miolo"]', { autoAlpha: 0, y: -18, duration: 0.4, ease: EASE.exit })
        .to(raiz.current, { yPercent: -100, duration: 0.85, ease: 'power4.inOut' }, '-=0.1');
    },
    { scope: raiz, dependencies: [saindo] },
  );

  // Entrada do conteúdo da cortina.
  useGSAP(
    () => {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      gsap.from('[data-pre="marca"]', { autoAlpha: 0, scale: 0.86, duration: 0.7, ease: EASE.signature });
      gsap.from('[data-pre="barra-caixa"]', { autoAlpha: 0, y: 14, duration: 0.6, delay: 0.15, ease: EASE.signature });
      // Respiração lenta na marca: dá sinal de vida sem competir com a barra,
      // que é quem carrega a informação.
      gsap.to('[data-pre="marca"]', {
        scale: 1.04,
        duration: 2.2,
        repeat: -1,
        yoyo: true,
        ease: 'sine.inOut',
      });
    },
    { scope: raiz },
  );

  return (
    <div
      ref={raiz}
      role="status"
      aria-live="polite"
      aria-label={`Carregando: ${pct}%`}
      className="ucc-grain fixed inset-0 z-[100] flex flex-col items-center justify-center bg-ink"
    >
      {/* Mesmo ambiente da hero, para a transição não parecer troca de site. */}
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="ucc-stadium-glow absolute inset-0" />
        <div className="ucc-grid-lines absolute inset-0 opacity-40" />
        <div className="absolute top-1/2 left-1/2 h-[36rem] w-[36rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-gold/[0.07] blur-[130px]" />
      </div>

      <div data-pre="miolo" className="relative flex flex-col items-center">
        <div data-pre="marca">
          <LogoUCC altura={86} />
        </div>

        <div data-pre="barra-caixa" className="mt-10 w-64">
          <div className="h-[3px] overflow-hidden rounded-full bg-surface-3">
            {/* A largura vem do estado, não de uma animação: é o número real. */}
            <div
              className="h-full rounded-full bg-linear-to-r from-gold-deep via-gold to-gold-bright transition-[width] duration-200 ease-out"
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className="mt-3 flex items-baseline justify-between">
            <span className="text-[0.6875rem] tracking-[0.2em] text-faint uppercase">
              Carregando
            </span>
            <span className="ucc-display tabular text-sm text-gold-ink">{pct}%</span>
          </div>
        </div>
      </div>

      <div className="absolute inset-x-0 bottom-0 h-px bg-linear-to-r from-transparent via-gold/30 to-transparent" />
    </div>
  );
}
